import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PortalUser } from 'src/app/core/domain/portal-user.model';

export interface UserOrganization {
  orgId: string;
  displayName: string;
  isActive: boolean;
}

/**
 * What the server already knows about a user who is about to register. `fromInvite` means a
 * pending TenantMembers row was matched by the caller's Auth0 email — the name in it is the one
 * the inviting admin typed, so the onboarding form has nothing left to ask.
 */
export interface RegistrationPrefill {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fromInvite: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly base = `${environment.apiUrl}/auth`;

  constructor(private http: HttpClient) {}

  getMe(): Observable<PortalUser> {
    return this.http.get<PortalUser>(`${this.base}/me`);
  }

  getRegistrationPrefill(): Observable<RegistrationPrefill> {
    return this.http.get<RegistrationPrefill>(`${this.base}/registration-prefill`);
  }

  completeRegistration(data: Partial<PortalUser>): Observable<void> {
    return this.http.post<void>(`${this.base}/complete-registration`, data);
  }

  updateProfile(data: Partial<PortalUser>): Observable<void> {
    return this.http.put<void>(`${this.base}/profile`, data);
  }

  updateLanguage(lang: string): Observable<void> {
    return this.updateProfile({ language: lang });
  }

  getOrganizations(): Observable<UserOrganization[]> {
    return this.http.get<UserOrganization[]>(`${this.base}/organizations`);
  }

  /** Set the user's preferred default tenant (by Auth0 org id). */
  setDefaultTenant(orgId: string): Observable<void> {
    return this.http.put<void>(`${this.base}/default-tenant`, { orgId });
  }

  /** Clear the user's preferred default tenant. */
  clearDefaultTenant(): Observable<void> {
    return this.http.delete<void>(`${this.base}/default-tenant`);
  }
}

